import React, { useCallback, useState, useMemo, useEffect } from 'react';
import {
  CommandSelect,
  CommandSelectTrigger,
  CommandSelectValue,
  CommandSelectContent,
  CommandSelectItem,
  CommandSelectAll,
  CommandSelectEmpty,
  CommandSelectLoading,
} from './command-select';
import { Member } from './types';

export interface MembersSelectProps {
  children: React.ReactNode;
}

export const MembersSelect: React.FC<MembersSelectProps> = ({ children }) => {
  return <div className="relative">{children}</div>;
};

export const MembersOptions = ({
  members,
  isLoading,
  value,
  onChange,
  onSearch,
}: {
  members: Member[];
  isLoading: boolean;
  value: string[];
  onChange: (ids: string[]) => void;
  onSearch?: (search: string) => void;
}) => {
  // State 1: Active filter items (comes from form submission via `value` prop)
  // We don't need to track this separately since it's just the `value` prop

  // State 2: Selected items to add (user selections before form submission)
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(value || []);
  const [selectedMembersData, setSelectedMembersData] = useState<Map<string, Member>>(new Map());
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync selectedMemberIds with value prop (for form initialization and updates)
  useEffect(() => {
    if (!isUpdating) {
      // Only update if arrays are actually different (deep equality check)
      const arraysAreEqual = selectedMemberIds.length === (value || []).length &&
        selectedMemberIds.every((id, index) => id === (value || [])[index]);

      if (!arraysAreEqual) {
        setSelectedMemberIds(value || []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isUpdating]); // selectedMemberIds intentionally excluded to prevent infinite loop

  // Initialize selected members data when members prop changes
  useEffect(() => {
    setSelectedMembersData((prevSelectedData) => {
      const newSelectedData = new Map<string, Member>();

      // Only keep members that are still selected
      selectedMemberIds.forEach((id) => {
        // First try to get fresh data from current members
        const freshMember = members.find((m) => m.id === id);
        if (freshMember) {
          newSelectedData.set(id, freshMember);
        } else {
          // If not found in current members, keep existing data if we have it
          const existingMember = prevSelectedData.get(id);
          if (existingMember) {
            newSelectedData.set(id, existingMember);
          }
        }
      });

      return newSelectedData;
    });
  }, [members, selectedMemberIds]);

  // Unified update handler with proper state management
  const updateSelection = useCallback(
    (newSelection: string[]) => {
      setIsUpdating(true);
      setSelectedMemberIds(newSelection);
      onChange(newSelection);

      // Reset updating flag after state has settled
      setTimeout(() => {
        setIsUpdating(false);
      }, 0);
    },
    [onChange],
  );

  const handleSelectMember = useCallback(
    (memberId: string) => {
      if (isUpdating) return;

      const isCurrentlySelected = selectedMemberIds.includes(memberId);

      if (isCurrentlySelected) {
        // Remove from selected
        const newSelectedIds = selectedMemberIds.filter((id) => id !== memberId);
        updateSelection(newSelectedIds);
      } else {
        // Add to selected
        const newSelectedIds = [...selectedMemberIds, memberId];
        updateSelection(newSelectedIds);
      }
    },
    [selectedMemberIds, updateSelection, isUpdating],
  );

  const handleDismiss = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      updateSelection([]);
      // Clear search when dismissing
      onSearch?.('');
    },
    [updateSelection, onSearch],
  );

  // Combine all states for display in the list
  const displayMembers = useMemo(() => {
    const memberMap = new Map<string, Member & { state: 'active' | 'selected' | 'search' }>();

    // Add search results (State 3)
    members.forEach((member) => {
      memberMap.set(member.id, { ...member, state: 'search' });
    });

    // Add selected members (State 2) - these override search results
    selectedMembersData.forEach((member, id) => {
      memberMap.set(id, { ...member, state: 'selected' });
    });

    return Array.from(memberMap.values());
  }, [members, selectedMembersData]);

  // Create Set for O(1) lookups during sorting
  const selectedMemberSet = useMemo(() => {
    return new Set(selectedMemberIds);
  }, [selectedMemberIds]);

  // Sort members: selected first, then search results
  const sortedMembers = useMemo(() => {
    return [...displayMembers].sort((a, b) => {
      const aSelected = selectedMemberSet.has(a.id);
      const bSelected = selectedMemberSet.has(b.id);

      // Selected items go to the top
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // If both selected or both unselected, maintain original order
      return 0;
    });
  }, [displayMembers, selectedMemberSet]);

  // Calculate select all state for currently visible members
  // Note: This works with displayMembers (hybrid of search results + selected members)
  const getSelectAllState = useMemo(() => {
    const visibleMemberIds = sortedMembers.map((m) => m.id);
    const selectedVisibleIds = selectedMemberIds.filter((id) => visibleMemberIds.includes(id));

    return {
      checked: visibleMemberIds.length > 0 && selectedVisibleIds.length === visibleMemberIds.length,
      indeterminate: selectedVisibleIds.length > 0 && selectedVisibleIds.length < visibleMemberIds.length,
      disabled: visibleMemberIds.length === 0 || isLoading || isUpdating
    };
  }, [sortedMembers, selectedMemberIds, isLoading, isUpdating]);

  // Handle select all/deselect all for currently visible members
  const handleSelectAllToggle = useCallback(() => {
    if (isUpdating || isLoading) {
      return;
    }

    const visibleMemberIds = sortedMembers.map((m) => m.id);
    const selectedVisibleIds = selectedMemberIds.filter((id) => visibleMemberIds.includes(id));

    let newSelection: string[];

    if (selectedVisibleIds.length === visibleMemberIds.length) {
      // Deselect all visible members (preserve selections from other search contexts)
      newSelection = selectedMemberIds.filter((id) => !visibleMemberIds.includes(id));
    } else {
      // Select all visible members (preserve existing selections)
      newSelection = Array.from(new Set([...selectedMemberIds, ...visibleMemberIds]));
    }

    updateSelection(newSelection);
  }, [sortedMembers, selectedMemberIds, updateSelection, isUpdating, isLoading]);

  const getDisplayText = () => {
    if (selectedMemberIds.length === 0) return { mainText: 'Select members...', count: null };

    if (selectedMemberIds.length === 1) {
      const member = selectedMembersData.get(selectedMemberIds[0]);
      return { mainText: member?.name || 'Unknown Member', count: null };
    }

    const firstMember = selectedMembersData.get(selectedMemberIds[0]);
    return {
      mainText: firstMember?.name || 'Unknown Member',
      count: selectedMemberIds.length - 1
    };
  };

  const handleSearchAndClearOnClose = useCallback(() => {
    // Clear search when closing dropdown
    onSearch?.('');
  }, [onSearch]);

  return (
    <CommandSelect onOpenChange={(open) => {
      if (!open) {
        handleSearchAndClearOnClose();
      }
    }}
    >
      <CommandSelectTrigger placeholder="Select members...">
        <CommandSelectValue
          placeholder="Select members..."
          displayText={selectedMemberIds.length > 0 ? getDisplayText().mainText : undefined}
          count={getDisplayText().count}
          onClear={handleDismiss}
          disabled={isUpdating}
        />
      </CommandSelectTrigger>
      <CommandSelectContent
        searchPlaceholder="Search members..."
        maxHeight="200px"
        useExternalSearch={!!onSearch}
        onSearchChange={onSearch}
      >
        {isLoading ? (
          <CommandSelectLoading>Loading members...</CommandSelectLoading>
        ) : (
          <>
            {/* Select All/Deselect All - shown when there are members available */}
            {(members.length > 0 || selectedMembersData.size > 0) && (
              <CommandSelectAll
                checked={getSelectAllState.checked}
                indeterminate={getSelectAllState.indeterminate}
                disabled={getSelectAllState.disabled}
                onToggle={handleSelectAllToggle}
                count={sortedMembers.length}
              />
            )}

            {sortedMembers.length > 0 ? (
              sortedMembers.map((member) => (
                <CommandSelectItem
                  key={member.id}
                  value={member.id}
                  onSelect={() => handleSelectMember(member.id)}
                  selected={selectedMemberIds.includes(member.id)}
                  disabled={isUpdating}
                >
                  <span>{member.name || 'Unknown Member'}</span>
                </CommandSelectItem>
              ))
            ) : (
              <CommandSelectEmpty>No members found.</CommandSelectEmpty>
            )}
          </>
        )}
      </CommandSelectContent>
    </CommandSelect>
  );
};
